import { Box, Checkbox, CircularProgress, FormControlLabel, FormGroup, IconButton, Select, SelectChangeEvent, Typography } from '@mui/material';
import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import ListAlt from '@mui/icons-material/ListAlt';
import { useTranslation } from 'react-i18next';

import { Reset } from 'components/icons/components';
import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';
import { SeismicAttribute } from 'features/seismic/models/interfaces/ISurveyMetadata';
import { ICalculatedAttribute } from 'features/seismic/models/interfaces/ICalculatedAttribute';
import { AttributeSettingsMenu, AttributeSettingsMenuProps } from './AttributeSettingsMenu';
import { UseAttributesStore } from 'features/seismic/stores/useAttributesStore';

export type SeismicAttributesSelectProps = {
    isClearable: boolean,
    title:string,
    attributes: SeismicAttribute[]|undefined,
    allAttributes: SeismicAttribute[]|undefined,
    selectedVolToken: string,
    setSelectedVolToken: (val: string) => void,
    selectedDefault: string,
    setOpenedPopup: (value: boolean) => void,
    baseProductName: string,
    setBaseProductName: (value: string) => void,
    setSelectedAttributes: (value: {
        baseProductName: string,
        all: SeismicAttribute[]
    } | undefined) => void;
    alineFeatureFlags: boolean,
    calculatedAttributes?: ICalculatedAttribute[],
    onSelectCalculatedAttribute?: (attribute: ICalculatedAttribute | null) => void,
    isDefaultSelectedCalculated?: boolean,
    attributeSettingsMenuProps?: AttributeSettingsMenuProps | undefined | null,
    disabled?: boolean,
};

export function SeismicAttributesSelect({isClearable, title, attributes, allAttributes, selectedDefault, setOpenedPopup, baseProductName, setBaseProductName,
    setSelectedAttributes, alineFeatureFlags, calculatedAttributes, selectedVolToken, setSelectedVolToken, onSelectCalculatedAttribute = (attribute) => {}, isDefaultSelectedCalculated = false,
    attributeSettingsMenuProps = null, disabled = false }
    : SeismicAttributesSelectProps){

    const { t } = useTranslation();

    const surveyMetadata = useSeismicStore(state => state.surveyMetadata);

    useEffect(() => console.log('SELECTED DEFAULT ==============>', selectedDefault, 'TITLE =========>', title), [selectedDefault]);

    const handleChange = ( event: SelectChangeEvent<string>) => {
        const selectedValue = event.target.value;

        if (!isClearable && allAttributes !== undefined && allAttributes !== null) {
            let productName = allAttributes?.find((x) => x.VolumeToken.toString() === selectedValue.toString());

            if (productName) {
                setBaseProductName(productName.Product);
                setSelectedAttributes({baseProductName: baseProductName, all: allAttributes});
            }
            else {
                setBaseProductName('');
                setSelectedAttributes({baseProductName: '', all: allAttributes});
            }
        }

        setSelectedVolToken(selectedValue);
    };

    const [ selectedCalculatedAttrToken, setSelectedCalculatedAttrToken ] = useState<string>('-');

    const handleCalculatedAttributeChange = useCallback((event: SelectChangeEvent<string>) => {
        const value = event.target.value;

        if (value === '-') {
            onSelectCalculatedAttribute(null);
        }
        else {
            const calculatedAttribute = calculatedAttributes?.find(x => x.Token === value);
            if (!!calculatedAttribute) {
                onSelectCalculatedAttribute(calculatedAttribute);
            }
        }

        setSelectedCalculatedAttrToken(value);
    }, [calculatedAttributes, onSelectCalculatedAttribute]);

    const { setAttributeVersion } = useSeismicStore(state => ({
        setAttributeVersion: state.setAttributeVersion,
    }));

    useEffect(() => {
        if (!isClearable) {
            let productName = attributes?.find((x) => x.VolumeToken.toString() === selectedDefault);
            const calculatedAttr = calculatedAttributes?.find(x => x.Token === selectedDefault);

            if (calculatedAttr) {
                //setSelectedCalculatedAttrToken(selectedDefault);
            }
            else if (productName) {
                setBaseProductName(productName.Product);
            }
            else if (!attributes || attributes.length === 0) {
                setSelectedVolToken(selectedDefault);
            }
            else {
                setBaseProductName('');
            }
        }
        if (isDefaultSelectedCalculated) {
            setSelectedCalculatedAttrToken(selectedDefault);
        }
        else if (selectedVolToken === '-') {
            setSelectedVolToken(selectedDefault);
        }
        //setSelectedAttrToken(`${isDefaultSelectedCalculated ? 'calculated' : 'stored'}-${selectedDefault}`);
    }
    ,[selectedDefault]);

    const selectedProduct = useMemo(() => attributes?.find((x) => x.VolumeToken.toString() === selectedVolToken.toString()), [attributes, selectedVolToken]);

    const openPopup = useCallback(() => {
        if (!!attributes) {
            setSelectedAttributes({baseProductName: selectedProduct?.Product ?? '', all: attributes});
            setOpenedPopup(true);
        }
    }, [attributes, selectedProduct?.Product, setOpenedPopup, setSelectedAttributes]);

    useEffect(() => console.log('SELECTED ATTR TOKEN ====>', selectedVolToken), [selectedVolToken]);

    return  (
        <Fragment>
            <Typography style={{color: '#ffffff'}} variant='body2'>{title}</Typography>
            <Box style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                {
                    (!!attributes && attributes.length > 0) || (!!calculatedAttributes && calculatedAttributes.length > 0)  ?
                        <Fragment>
                            <Select fullWidth style={{minWidth:'120px', fontSize: '12px', color:'#ffffff', padding:'3px', marginRight: '5px'}} value={selectedVolToken} native onChange={handleChange} disabled={disabled}>
                                {
                                    isClearable ?
                                        <option style={{padding: '10px', color: '#000000'}} key={'product-' + title + '-' + 'no-overlay'} value='-'>{'No ' + title}</option>
                                        :
                                        !attributes?
                                            <option style={{padding: '10px', color: '#000000'}} key={'-'}>{surveyMetadata?.DisplayName}</option>
                                            :
                                            null
                                }
                                {
                                    (!!attributes && attributes.length === 0 && !!surveyMetadata)
                                    &&
                                    <option style={{padding: '10px', color: '#000000'}} key={'default'} value={surveyMetadata.VolumeToken}>{`${surveyMetadata.DisplayName}_${surveyMetadata.Domain.toString()}`}</option>
                                }
                                {
                                    attributes?.map(el => <option style={{padding: '10px', color: '#000000'}} key={'product-' + title + '-' + el.VolumeToken} value={el.VolumeToken}>{el.Product}</option>)
                                }

                            </Select>
                            {
                                !!calculatedAttributes
                                &&
                                calculatedAttributes.length > 0
                                &&
                                <Select
                                    onChange={handleCalculatedAttributeChange}
                                    value={selectedCalculatedAttrToken}
                                    native
                                    fullWidth
                                    disabled={isClearable && selectedVolToken === '-'}
                                    style={{fontSize: '12px', color:'#ffffff', padding:'3px', marginRight: '5px'}}
                                >
                                    <option style={{padding: '10px', color: '#000000'}} key={'-'} value={'-'}> Default </option>
                                    {
                                        calculatedAttributes?.map(el =>  <option style={{padding: '10px', color: '#000000'}} key={el.Token} value={el.Token}>{el.Title}</option>)
                                    }
                                </Select>
                            }

                            {/* <Box style={{backgroundColor: '#ffffff', borderRadius: '5px', cursor: 'pointer'}} onClick={() => openPopup()} title={t('checkGrid')}> */}
                            {(title === 'Overlay' && alineFeatureFlags) &&
                                <IconButton
                                    color='primary'
                                    onClick={() => setAttributeVersion()}
                                >
                                    <Reset />
                                </IconButton >
                            }
                            <IconButton
                                id='attributes'
                                color='primary'
                                onClick={() => openPopup()} title={'List details from available products'}
                            >
                                <ListAlt />
                            </IconButton>
                        </Fragment>
                        :
                        <Fragment>
                            <Typography padding={'10px'} color='white' variant='caption'>No items available.</Typography>
                            <IconButton
                                id='attributes'
                                color='primary'
                                onClick={() => openPopup()} title={'List details from available products'}
                            >
                                <ListAlt />
                            </IconButton>
                        </Fragment>
                }
                {
                    !!attributeSettingsMenuProps
                    &&
                    <AttributeSettingsMenu
                        {...attributeSettingsMenuProps}
                        disabled={(selectedVolToken === '-' && selectedCalculatedAttrToken === '-') || disabled}
                    />
                }
                {/* </Box> */}
            </Box>
        </Fragment>
    );
}

